package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.arena_web.data.api.SupportApi
import com.arena_web.data.http.HttpClientFactory
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.s
import com.arena_web.ui.utils.sBorderRadius
import com.arena_web.ui.utils.sFontSize
import com.arena_web.ui.utils.sGap
import com.arena_web.ui.utils.sHeight
import com.arena_web.ui.utils.sLetterSpacing
import com.arena_web.ui.utils.sLineHeight
import com.arena_web.ui.utils.sMargin
import com.arena_web.ui.utils.sMaxWidth
import com.arena_web.ui.utils.sPadding
import com.arena_web.ui.utils.sSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

private const val URBANIST = AppTypography.PRIMARY_FONT_NAME
private const val URBANIST_FB = AppTypography.PRIMARY_FONT_FALLBACK

// Direct support contacts, shown alongside the form.
private const val SUPPORT_PHONE_DISPLAY = "+380 67 565 5462"
private const val SUPPORT_PHONE_URI = "tel:+380675655462"
private const val SUPPORT_EMAIL = "inowioss@gmail.com"

private enum class SendState { Idle, Sending, Sent }

/**
 * "Contact Support" dialog opened from the footer. Posts the message to
 * `v1/support/messages` (public endpoint); admins read them later from the
 * admin panel.
 */
@Composable
fun SupportPopup(
    isOpen: Boolean,
    onClose: () -> Unit,
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var email by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var state by remember { mutableStateOf(SendState.Idle) }
    val scope = rememberCoroutineScope()

    fun send() {
        if (state != SendState.Idle) return
        val trimmedEmail = email.trim()
        val trimmedMessage = message.trim()
        when {
            trimmedEmail.isBlank() || !trimmedEmail.contains("@") || !trimmedEmail.contains(".") -> {
                error = t.supportErrorInvalidEmail
                return
            }
            trimmedMessage.isBlank() -> {
                error = t.supportErrorEmptyMessage
                return
            }
        }
        error = null
        state = SendState.Sending
        scope.launch {
            val response = runCatching {
                SupportApi(HttpClientFactory.createDefault()).sendMessage(trimmedEmail, trimmedMessage)
            }.getOrNull()
            if (response?.data != null) {
                state = SendState.Sent
                window.setTimeout({ onClose() }, 1800)
            } else {
                state = SendState.Idle
                error = response?.errors?.firstOrNull()
                    ?: t.supportErrorGeneric
            }
        }
    }

    // Dimmed, blurred backdrop
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "1000")
                property("background", "rgba(3, 7, 18, 0.66)")
                property("backdrop-filter", "blur(8px)")
                property("-webkit-backdrop-filter", "blur(8px)")
            }
            .onClick { onClose() }
            .animation(
                ModalOverlayFadeInKeyframes.toAnimation(
                    duration = 200.ms,
                    timingFunction = AnimationTimingFunction.EaseOut,
                )
            ),
        contentAlignment = Alignment.Center,
    ) {
        // Card — dark blue surface with the project's gradient border ring
        Box(
            modifier = Modifier
                .sMaxWidth(520)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(24)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("position", "relative")
                    property(
                        "background",
                        "linear-gradient(-115deg, rgba(0, 59, 174, 0.30), rgba(3, 87, 230, 0.10)), #0A101F"
                    )
                    property("box-shadow", "0 32px 80px rgba(0, 0, 0, 0.55)")
                }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = 260.ms,
                        timingFunction = AnimationTimingFunction.EaseOut,
                    )
                ),
        ) {
            // Gradient border ring (mask-composite trick — project rule for dialogs)
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(24))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(32, 36)
                    .sGap(20)
                    .attrsModifier {
                        ref { _ -> injectSupportPopupStyles(); onDispose {} }
                    },
            ) {
                if (state == SendState.Sent) {
                    SentConfirmation()
                } else {
                    PopupHeader(onClose)

                    SpanText(
                        t.supportIntro,
                        modifier = Modifier
                            .sFontSize(15)
                            .sLineHeight(22)
                            .fontFamily(URBANIST, URBANIST_FB)
                            .fontWeight(AppTypography.Regular)
                            .styleModifier { property("color", "#8FA8CC") }
                    )

                    ContactLinksRow()

                    FieldLabel(t.supportEmailLabel)
                    Input(type = InputType.Email) {
                        value(email)
                        onInput { email = it.value; error = null }
                        attr("placeholder", "you@example.com")
                        classes("support-input")
                    }

                    FieldLabel(t.supportMessageLabel)
                    TextArea(value = message) {
                        onInput { message = it.value; error = null }
                        attr("placeholder", t.supportMessagePlaceholder)
                        attr("rows", "5")
                        classes("support-input", "support-textarea")
                    }

                    if (error != null) {
                        SpanText(
                            error!!,
                            modifier = Modifier
                                .sFontSize(14)
                                .fontFamily(URBANIST, URBANIST_FB)
                                .styleModifier { property("color", "#FF6B6B") }
                        )
                    }

                    SendButton(
                        sending = state == SendState.Sending,
                        onClick = ::send,
                    )
                }
            }
        }
    }
}

@Composable
private fun PopupHeader(onClose: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            LocalAppStrings.current.supportTitle,
            modifier = Modifier
                .sFontSize(26)
                .sLineHeight(32)
                .sLetterSpacing(0.5)
                .fontWeight(AppTypography.Bold)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "#FFFFFF")
                    property("flex-grow", "1")
                }
        )
        var hovered by remember { mutableStateOf(false) }
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onClose() }
                .onMouseEnter { hovered = true }
                .onMouseLeave { hovered = false }
                .styleModifier {
                    property("background", if (hovered) "rgba(190, 214, 245, 0.16)" else "rgba(190, 214, 245, 0.08)")
                    property("transition", "background 150ms ease-out")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                "×",
                modifier = Modifier
                    .sFontSize(22)
                    .styleModifier {
                        property("color", "var(--arena-text-2)")
                        property("line-height", "1")
                        property("user-select", "none")
                    }
            )
        }
    }
}

@Composable
private fun ContactLinksRow() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(24)
            .styleModifier { property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ContactLink(AppIcons.Phone, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_URI)
        ContactLink(AppIcons.Envelope, SUPPORT_EMAIL, "mailto:$SUPPORT_EMAIL")
    }
}

@Composable
private fun ContactLink(icon: String, text: String, href: String) {
    A(
        href = href,
        attrs = { style { property("text-decoration", "none") } },
    ) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = icon, modifier = Modifier.sSize(16))
            SpanText(
                text,
                modifier = Modifier
                    .sFontSize(15)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Medium)
                    .styleModifier { property("color", "var(--arena-text-2)") }
            )
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(14)
            .sLetterSpacing(0.3)
            .fontWeight(AppTypography.Medium)
            .fontFamily(URBANIST, URBANIST_FB)
            .styleModifier {
                property("color", "var(--arena-text-2)")
                property("margin-bottom", s(-12))
            }
    )
}

@Composable
private fun SendButton(sending: Boolean, onClick: () -> Unit) {
    var hovered by remember { mutableStateOf(false) }
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(52)
            .sBorderRadius(14)
            .cursor(if (sending) Cursor.Default else Cursor.Pointer)
            .onClick { if (!sending) onClick() }
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("background", "linear-gradient(180deg, #1668FF 0%, #0048CC 100%)")
                property(
                    "box-shadow",
                    if (hovered && !sending) "0 10px 28px rgba(0, 97, 255, 0.45)"
                    else "0 6px 18px rgba(0, 97, 255, 0.25)"
                )
                property("transform", if (hovered && !sending) "translateY(-1px)" else "none")
                property("transition", "transform 150ms ease-out, box-shadow 150ms ease-out, opacity 150ms ease-out")
                property("opacity", if (sending) "0.7" else "1")
                property("user-select", "none")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            if (sending) LocalAppStrings.current.supportSending else LocalAppStrings.current.supportSendMessage,
            modifier = Modifier
                .sFontSize(17)
                .sLetterSpacing(0.4)
                .fontWeight(AppTypography.SemiBold)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier { property("color", "#FFFFFF") }
        )
    }
}

@Composable
private fun SentConfirmation() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(24, 0).sGap(14),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .sSize(56)
                .sBorderRadius(100)
                .styleModifier {
                    property("background", "rgba(46, 204, 113, 0.15)")
                    property("border", "1px solid rgba(46, 204, 113, 0.45)")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                "✓",
                modifier = Modifier
                    .sFontSize(26)
                    .styleModifier { property("color", "#2ECC71") }
            )
        }
        SpanText(
            t.supportSentTitle,
            modifier = Modifier
                .sFontSize(22)
                .fontWeight(AppTypography.Bold)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier { property("color", "#FFFFFF") }
        )
        SpanText(
            t.supportSentMessage,
            modifier = Modifier
                .sFontSize(15)
                .sLineHeight(22)
                .fontFamily(URBANIST, URBANIST_FB)
                .styleModifier {
                    property("color", "#8FA8CC")
                    property("text-align", "center")
                }
        )
    }
}

// Input/textarea styling lives in an injected stylesheet so :focus and
// ::placeholder work (inline styles can't express pseudo-classes).
private fun injectSupportPopupStyles() {
    val id = "support-popup-styles"
    if (document.getElementById(id) != null) return
    val style = document.createElement("style")
    style.id = id
    style.textContent = """
        .support-input {
            width: 100%;
            box-sizing: border-box;
            padding: ${s(14)} ${s(16)};
            border-radius: ${s(12)};
            font-size: ${s(16)};
            font-family: '$URBANIST', $URBANIST_FB;
            color: #FFFFFF;
            background: rgba(190, 214, 245, 0.06);
            border: 1px solid rgba(190, 214, 245, 0.16);
            outline: none;
            transition: border-color 160ms ease-out, box-shadow 160ms ease-out;
        }
        .support-input::placeholder {
            color: rgba(143, 168, 204, 0.55);
        }
        .support-input:focus {
            border-color: rgba(0, 97, 255, 0.85);
            box-shadow: 0 0 0 3px rgba(0, 97, 255, 0.18);
        }
        .support-textarea {
            resize: vertical;
            min-height: ${s(120)};
        }
    """.trimIndent()
    document.head?.appendChild(style)
}
